import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Star } from 'lucide-react';

const TemplatesPage = () => {
  // Mock template data
  const templates = [
    {
      id: 1,
      title: 'Modern Business',
      category: 'Business',
      platform: 'LinkedIn',
      preview: '/api/placeholder/300/400',
      rating: 4.8,
      downloads: 1234,
      isPremium: false,
    },
    {
      id: 2,
      title: 'Social Media Tips',
      category: 'Marketing',
      platform: 'Instagram',
      preview: '/api/placeholder/300/400',
      rating: 4.9,
      downloads: 2156,
      isPremium: true,
    },
    {
      id: 3,
      title: 'Tech Startup',
      category: 'Technology',
      platform: 'LinkedIn',
      preview: '/api/placeholder/300/400',
      rating: 4.7,
      downloads: 987,
      isPremium: false,
    },
    {
      id: 4,
      title: 'Creative Portfolio',
      category: 'Design',
      platform: 'Instagram',
      preview: '/api/placeholder/300/400',
      rating: 4.6,
      downloads: 756,
      isPremium: true,
    },
  ];

  const categories = ['All', 'Business', 'Marketing', 'Technology', 'Design', 'Education'];
  const platforms = ['All Platforms', 'Instagram', 'LinkedIn', 'Facebook', 'Twitter'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Carousel Templates
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Choose from hundreds of professionally designed templates to create 
          stunning carousel posts in minutes.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex gap-4">
          <select className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <select className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
            {platforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
          
          <button className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-5 w-5" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="card overflow-hidden group hover:shadow-lg transition-shadow duration-200">
            {/* Template Preview */}
            <div className="relative aspect-[3/4] bg-gray-100">
              <img
                src={template.preview}
                alt={template.title}
                className="w-full h-full object-cover"
              />
              {template.isPremium && (
                <div className="absolute top-3 left-3">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-medium px-2 py-1 rounded-full">
                    Premium
                  </span>
                </div>
              )}
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                <Link
                  to={`/editor?template=${template.id}`}
                  className="opacity-0 group-hover:opacity-100 btn-primary transform scale-90 group-hover:scale-100 transition-all duration-200"
                >
                  Use Template
                </Link>
              </div>
            </div>

            {/* Template Info */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {template.title}
                </h3>
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-gray-600">{template.rating}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                  {template.category}
                </span>
                <span>{template.downloads.toLocaleString()} downloads</span>
              </div>
              
              <div className="mt-2 text-xs text-gray-500">
                Optimized for {template.platform}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      <div className="text-center mt-12">
        <button className="btn-outline px-8 py-3">
          Load More Templates
        </button>
      </div>

      {/* CTA Section */}
      <div className="mt-16 bg-primary-50 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Can't find the perfect template?
        </h2>
        <p className="text-gray-600 mb-6">
          Start with a blank canvas and create your own unique design from scratch.
        </p>
        <Link to="/editor" className="btn-primary">
          Start from Scratch
        </Link>
      </div>
    </div>
  );
};

export default TemplatesPage;
